import React, {useState} from 'react';
import {Redirect, Route, Switch} from 'react-router-dom';
import {Routes} from '../routes';
import Utils from '../services/Utils';

// pages
import DashboardOverview from './Dashboard';
import Orders from './Orders';
import Customers from './Customers';
import Planning from './Planning';
import Settings from './Settings';
import Vehicles from './Vehicles';
import Packages from './Packages';
import Surcharges from './Surcharges';
import Options from './Options';
import DiscountCodes from './DiscountCodes';
import HtmlIntegration from './HtmlIntegration';
import Signin from './login/Signin';
import ForgotPassword from './login/ForgotPassword';
import ResetPassword from './login/ResetPassword';
import NotFoundPage from './login/NotFound';
import Booking from './booking/Booking';
import PaymentSuccess from './booking/PaymentSuccess';
import PaymentCancel from './booking/PaymentCancel';
import BookingConfirmed from './booking/BookingConfirmed';
import BookingCanceled from './booking/BookingCanceled';
import Signup from './subscription/Signup';
import SubscriptionSuccess from './subscription/SubscriptionSuccess';

// components
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Preloader, {useAppLoading} from '../components/Preloader';
import BookingCreateModal from '../components/BookingCreateModal';
import BookingModal from '../components/BookingModal';

import {createIntl, createIntlCache, IntlProvider} from 'react-intl';

import messages_fr from '../locales/fr.json';
import messages_en from '../locales/en.json';
import messages_es from '../locales/es.json';
import messages_it from '../locales/it.json';
import messages_de from '../locales/de.json';
import messages_pl from '../locales/pl.json';
import {ReactNotifications} from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';
import '../index.css';
import API from '../services/API';
import {API_URL} from '../services/constants';
import {useIntl} from '../services/IntlContext';
import {NotificationsProvider} from '../services/NotificationsContext';
import {PlanProvider} from '../services/PlanContext';
import BookingFields from './BookingFields';

// Translations
const messages = {
  'fr': messages_fr,
  'en': messages_en,
  'es': messages_es,
  'it': messages_it,
  'de': messages_de,
  'pl': messages_pl
};
const language = Utils.getLanguage();
const cache = createIntlCache(); // This is optional but highly recommended since it prevents memory leak
const intl = createIntl({
  locale: language,
  messages: messages[language],
  defaultLocale: 'fr'
}, cache);

const RouteWithLoader = ({component: Component, ...rest}) => {
  const loading = useAppLoading();
  const {intl, switchLanguage} = useIntl();

  return (
    <Route {...rest} render={props => (<> <Preloader show={loading}/> <Component {...rest} {...props} intl={intl} switchLanguage={switchLanguage}/> </>)}/>
  );
};

const RouteWithSidebar = ({component: Component, ...rest}) => {
  const loading = useAppLoading();
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  // Booking opened from the global search — shown via layout-level BookingModal
  // so the user stays on the current page instead of bouncing to /admin/orders.
  // We track visibility separately from the booking itself: BookingModal's Share
  // / Confirm / Edit buttons call our onClose to hide the details pane, then
  // open a sibling modal — so BookingModal (and its child BookingShareModal etc.)
  // must stay mounted across that transition. Unmounting on close would lose the
  // sibling modals in flight.
  const [focusedBooking, setFocusedBooking] = useState(null);
  const [showFocusedBooking, setShowFocusedBooking] = useState(false);
  const {intl} = useIntl();

  return (
    <Route {...rest} render={props => (
      <NotificationsProvider intl={intl}>
        <Preloader show={loading}/>
        <Sidebar intl={intl}/>

        <main className="content">
          <Navbar intl={intl}
                  onCreateBooking={() => setShowCreateBooking(true)}
                  onOpenBooking={(booking) => { setFocusedBooking(booking); setShowFocusedBooking(true); }}/>
          <Component {...rest} {...props} intl={intl}/>
          {/*<Footer toggleSettings={toggleSettings} showSettings={showSettings}/>*/}
        </main>

        {showCreateBooking && (
          <BookingCreateModal
            intl={intl}
            show={showCreateBooking}
            onClose={() => setShowCreateBooking(false)}
            onRefresh={() => props.history.push(Routes.Orders.path)}
          />
        )}

        {focusedBooking && (
          <BookingModal
            intl={intl}
            booking={focusedBooking}
            show={showFocusedBooking}
            onClose={() => setShowFocusedBooking(false)}
            onRefresh={() => {}}
          />
        )}
      </NotificationsProvider>
    )}
    />
  );
};

const RenderDynamicModule = ({match, history, location}) => {
  const {moduleName} = match.params;

  React.useEffect(() => {
    const regex = /^\/[a-zA-Z0-9_-]+?$/;
    const path = location.pathname;

    if (regex.test(path)) {
      const fetchModuleContent = async () => {
        try {
          const response = await API.getModuleIdentifierByName(moduleName);

          if (response.status === 200) {
            document.body.innerHTML = '<iframe src=\'' + API_URL + '/module/' + response.data + '\'' +
              'style="overflow:hidden;height:100%;width:100%;position:absolute;top:0;left:0;right:0;bottom:0" height="100%" width="100%"></iframe>'
          }
        } catch (ignored) {

        }
      };

      fetchModuleContent();
    } else {
      history.push(Routes.NotFound.path);
    }
  }, [match.url, moduleName, history]);

  return null;
};

export default () => (
  <IntlProvider locale={language} messages={messages[language]} defaultLocale="fr">
    <ReactNotifications/>
    <PlanProvider intl={intl}>
    <Switch>
      <RouteWithLoader exact path={Routes.Root.path} component={Signin}/>
      <RouteWithLoader exact path={Routes.Signin.path} component={Signin}/>
      <RouteWithLoader exact path={Routes.ForgotPassword.path} component={ForgotPassword}/>
      <RouteWithLoader exact path={Routes.ResetPassword.path} component={ResetPassword}/>
      <RouteWithLoader exact path={Routes.NotFound.path} component={NotFoundPage}/>
      <RouteWithLoader exact path={Routes.Booking.path} component={Booking}/>
      <RouteWithLoader exact path={Routes.PaymentSuccess.path} component={PaymentSuccess}/>
      <RouteWithLoader exact path={Routes.PaymentCancel.path} component={PaymentCancel}/>
      <RouteWithLoader exact path={Routes.BookingConfirmed.path} component={BookingConfirmed}/>
      <RouteWithLoader exact path={Routes.BookingCanceled.path} component={BookingCanceled}/>
      <RouteWithLoader exact path={Routes.Signup.path} component={Signup}/>
      <RouteWithLoader exact path={Routes.SubscriptionSuccess.path} component={SubscriptionSuccess}/>

      {/* pages */}
      <RouteWithSidebar exact path={Routes.DashboardOverview.path} component={DashboardOverview}/>
      <RouteWithSidebar exact path={Routes.Orders.path} component={Orders}/>
      <RouteWithSidebar exact path={Routes.Customers.path} component={Customers}/>
      <RouteWithSidebar exact path={Routes.Planning.path} component={Planning}/>
      <RouteWithSidebar exact path={Routes.Settings.path} component={Settings}/>
      <RouteWithSidebar exact path={Routes.Vehicles.path} component={Vehicles}/>
      <RouteWithSidebar exact path={Routes.Packages.path} component={Packages}/>
      <RouteWithSidebar exact path={Routes.Surcharges.path} component={Surcharges}/>
      <RouteWithSidebar exact path={Routes.Options.path} component={Options}/>
      <RouteWithSidebar exact path={Routes.DiscountCodes.path} component={DiscountCodes}/>
      <RouteWithSidebar exact path={Routes.BookingFields.path} component={BookingFields}/>
      <RouteWithSidebar exact path={Routes.HtmlIntegration.path} component={HtmlIntegration}/>

      <Route path="/:moduleName" render={(props) => <RenderDynamicModule {...props} />}/>
      <Route path="*" render={() => <Redirect to={Routes.NotFound.path}/>}/>

    </Switch>
      </PlanProvider>
  </IntlProvider>
);
